// Application Components
import React, { useState, useEffect } from 'react'
import parse from 'html-react-parser'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { object, string } from 'yup'
import { translateMultiFormField } from '../../functions/field-functions'
import {
    subtractValuesAdditionalArrayForm,
    subtractValuesAdditionalConditionalForm,
    extractSubmissionForm,
} from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormAuxiliary,
    deleteFormData,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components

import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import FileUploadComponent from '../../components/file-upload-components/file-upload-component'
import AddFormComponent from '../../components/navigation-components/add-form-component'
import AdditionalCriminalHistoryComponent from '../../components/generic-components/additional-criminal-history-component'

//// Carbon Components
import {
    Form,
    RadioButtonGroup,
    RadioButton,
    TextInput,
    TextArea,
    Select,
    SelectItem,
    DatePicker,
    DatePickerInput,
} from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const CriminalHistoryForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const category_digital_name =
        settingsData?.['form']?.['category_digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']
    const max_form_iterations =
        settingsData?.['application']?.['max_form_iterations']
    const conditions = settingsData?.['form']?.['conditions']

    //////// Search Params

    //////// Methods

    // Parse RSD83 Label
    const RSD83Label = () => {
        const menu_item_label = `<p>Have you ever been convicted of, or entered a plea of guilty, nolo contendere, or no contest to any crime in any jurisdiction other than a minor traffic offense? You must include all misdemeanors and felonies, even if adjudication was withheld.</p><p>Reckless driving, driving while license suspended or revoked (DWLSR), driving under the influence (DUI) or driving while impaired (DWI) are not minor traffic offenses for purposes of this question.</p>`
        if (menu_item_label) {
            const label = parse(`${menu_item_label}`)
            if (label) {
                return label
            } else {
                return null
            }
        } else {
            return null
        }
    }

    // Generate Validation Schema
    const generateSchema = async (auxAddtForms) => {
        try {
            const dynamic = {}
            if (category_digital_name.includes('registered-nurse')) {
                if (auxAddtForms?.length > 0) {
                    dynamic.addtObjs = [
                        {
                            RSD40: '',
                            RSD42: '',
                            RSD41: '',
                            FIN_DISP: '',
                            RSD43: '',
                            LIC_NTS: '',
                        },
                    ]
                    dynamic.addtFilds = [
                        {
                            RSD40: false,
                            RSD40ErrMsg: '',
                            RSD42: false,
                            RSD42ErrMsg: '',
                            RSD41: false,
                            RSD41ErrMsg: '',
                            FIN_DISP: false,
                            FIN_DISPErrMsg: '',
                            RSD43: false,
                            RSD43ErrMsg: '',
                            LIC_NTS: false,
                            LIC_NTSErrMsg: '',
                        },
                    ]
                    dynamic.addtObjs[0] = {
                        ...dynamic.addtObjs[0],
                        RSD83: '',
                        RSD40: '',
                        RSD42: '',
                        RSD41: '',
                        FIN_DISP: '',
                        RSD43: '',
                        LIC_NTS: '',
                    }
                    dynamic.addtFilds[0] = {
                        ...dynamic.addtFilds[0],
                        RSD83: false,
                        RSD83ErrMsg: '',
                        RSD40: false,
                        RSD40ErrMsg: '',
                        RSD42: false,
                        RSD42ErrMsg: '',
                        RSD41: false,
                        RSD41ErrMsg: '',
                        FIN_DISP: false,
                        FIN_DISPErrMsg: '',
                        RSD43: false,
                        RSD43ErrMsg: '',
                        LIC_NTS: false,
                        LIC_NTSErrMsg: '',
                    }
                    await auxAddtForms.forEach(async (el) => {
                        dynamic.addtObjs.push({
                            [`RSD40` + el]: '',
                            [`RSD42` + el]: '',
                            [`RSD41` + el]: '',
                            [`FIN_DISP` + el]: '',
                            [`RSD43` + el]: '',
                            [`LIC_NTS` + el]: '',
                        })
                        dynamic.addtFilds.push({
                            [`RSD40` + el]: '',
                            [`RSD40` + el + 'ErrMsg']: false,
                            [`RSD42` + el]: '',
                            [`RSD42` + el + 'ErrMsg']: false,
                            [`RSD41` + el]: '',
                            [`RSD41` + el + 'ErrMsg']: false,
                            [`FIN_DISP` + el]: '',
                            [`FIN_DISP` + el + 'ErrMsg']: false,
                            [`RSD43` + el]: '',
                            [`RSD43` + el + 'ErrMsg']: false,
                            [`LIC_NTS` + el]: '',
                            [`LIC_NTS` + el + 'ErrMsg']: false,
                        })
                    })
                    dynamic.addtObjsSchema = {}
                    if (dynamic?.addtObjs?.length > 0) {
                        dynamic.addtObjs.forEach(async (el) => {
                            dynamic.addtObjsSchema = {
                                ...dynamic.addtObjsSchema,
                                ...el,
                            }
                        })
                    }
                    dynamic.addtFildsSchema = {}
                    if (dynamic?.addtFilds?.length > 0) {
                        dynamic.addtFilds.forEach(async (el) => {
                            dynamic.addtFildsSchema = {
                                ...dynamic.addtFildsSchema,
                                ...el,
                            }
                        })
                    }
                    dynamic.addtObjsSchemaKeys = Object?.keys(
                        dynamic.addtObjsSchema
                    )
                    dynamic.addtFildsSchemaKeys = Object?.keys(
                        dynamic.addtFildsSchema
                    )
                    dynamic.addtSchema = {}
                    if (dynamic?.addtObjsSchemaKeys?.length > 0) {
                        dynamic.addtObjsSchemaKeys.forEach(async (el) => {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().required(
                                    'This field is required to be entered or selected.'
                                ),
                            }
                        })
                    }
                    dynamic.addtValid = {}
                    if (dynamic?.addtFildsSchemaKeys?.length > 0) {
                        dynamic.addtFildsSchemaKeys.forEach(async (el) => {
                            if (el?.includes('ErrMsg')) {
                                dynamic.addtValid = {
                                    ...dynamic.addtValid,
                                    [el]: '',
                                }
                            } else {
                                dynamic.addtValid = {
                                    ...dynamic.addtValid,
                                    [el]: false,
                                }
                            }
                        })
                    }
                }
            } else if (
                category_digital_name.includes('licensed-practical-nurse')
            ) {
                if (auxAddtForms?.length > 0) {
                    dynamic.addtObjs = [
                        {
                            RSD40: '',
                            RSD42: '',
                            RSD41: '',
                            FINAL_DISP: '',
                            RSD43: '',
                            LIC_NTS: '',
                        },
                    ]
                    dynamic.addtFilds = [
                        {
                            RSD40: false,
                            RSD40ErrMsg: '',
                            RSD42: false,
                            RSD42ErrMsg: '',
                            RSD41: false,
                            RSD41ErrMsg: '',
                            FINAL_DISP: false,
                            FINAL_DISPErrMsg: '',
                            RSD43: false,
                            RSD43ErrMsg: '',
                            LIC_NTS: false,
                            LIC_NTSErrMsg: '',
                        },
                    ]
                    dynamic.addtObjs[0] = {
                        ...dynamic.addtObjs[0],
                        RSD83: '',
                        RSD40: '',
                        RSD42: '',
                        RSD41: '',
                        FINAL_DISP: '',
                        RSD43: '',
                        LIC_NTS: '',
                    }
                    dynamic.addtFilds[0] = {
                        ...dynamic.addtFilds[0],
                        RSD83: false,
                        RSD83ErrMsg: '',
                        RSD40: false,
                        RSD40ErrMsg: '',
                        RSD42: false,
                        RSD42ErrMsg: '',
                        RSD41: false,
                        RSD41ErrMsg: '',
                        FINAL_DISP: false,
                        FINAL_DISPErrMsg: '',
                        RSD43: false,
                        RSD43ErrMsg: '',
                        LIC_NTS: false,
                        LIC_NTSErrMsg: '',
                    }
                    await auxAddtForms.forEach(async (el) => {
                        dynamic.addtObjs.push({
                            [`RSD40` + el]: '',
                            [`RSD42` + el]: '',
                            [`RSD41` + el]: '',
                            [`FINAL_DISP` + el]: '',
                            [`RSD43` + el]: '',
                            [`LIC_NTS` + el]: '',
                        })
                        dynamic.addtFilds.push({
                            [`RSD40` + el]: '',
                            [`RSD40` + el + 'ErrMsg']: false,
                            [`RSD42` + el]: '',
                            [`RSD42` + el + 'ErrMsg']: false,
                            [`RSD41` + el]: '',
                            [`RSD41` + el + 'ErrMsg']: false,
                            [`FINAL_DISP` + el]: '',
                            [`FINAL_DISP` + el + 'ErrMsg']: false,
                            [`RSD43` + el]: '',
                            [`RSD43` + el + 'ErrMsg']: false,
                            [`LIC_NTS` + el]: '',
                            [`LIC_NTS` + el + 'ErrMsg']: false,
                        })
                    })
                    dynamic.addtObjsSchema = {}
                    if (dynamic?.addtObjs?.length > 0) {
                        dynamic.addtObjs.forEach(async (el) => {
                            dynamic.addtObjsSchema = {
                                ...dynamic.addtObjsSchema,
                                ...el,
                            }
                        })
                    }
                    dynamic.addtFildsSchema = {}
                    if (dynamic?.addtFilds?.length > 0) {
                        dynamic.addtFilds.forEach(async (el) => {
                            dynamic.addtFildsSchema = {
                                ...dynamic.addtFildsSchema,
                                ...el,
                            }
                        })
                    }
                    dynamic.addtObjsSchemaKeys = Object?.keys(
                        dynamic.addtObjsSchema
                    )
                    dynamic.addtFildsSchemaKeys = Object?.keys(
                        dynamic.addtFildsSchema
                    )
                    dynamic.addtSchema = {}
                    if (dynamic?.addtObjsSchemaKeys?.length > 0) {
                        dynamic.addtObjsSchemaKeys.forEach(async (el) => {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().required(
                                    'This field is required to be entered or selected.'
                                ),
                            }
                        })
                    }
                    dynamic.addtValid = {}
                    if (dynamic?.addtFildsSchemaKeys?.length > 0) {
                        dynamic.addtFildsSchemaKeys.forEach(async (el) => {
                            if (el?.includes('ErrMsg')) {
                                dynamic.addtValid = {
                                    ...dynamic.addtValid,
                                    [el]: '',
                                }
                            } else {
                                dynamic.addtValid = {
                                    ...dynamic.addtValid,
                                    [el]: false,
                                }
                            }
                        })
                    }
                }
            }
            return dynamic
        } catch (error) {
            console.warn('Generate Schema Error:', error)
            return null
        }
    }

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (
                formsData?.[digital_name]?.values?.RSD83 === undefined ||
                formsData?.[digital_name]?.values?.RSD83 === 'N'
            ) {
                schema.yup = object({
                    RSD83: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    RSD83: false,
                    RSD83ErrMsg: '',
                }
            } else if (
                category_digital_name.includes('registered-nurse') &&
                formsData?.[digital_name]?.values?.RSD83 === 'Y' &&
                formsData[digital_name].auxiliary.addtForms?.length === 0
            ) {
                schema.yup = object({
                    RSD83: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RSD40: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RSD42: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RSD41: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    FIN_DISP: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RSD43: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    LIC_NTS: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    RSD83: false,
                    RSD83ErrMsg: '',
                    RSD40: false,
                    RSD40ErrMsg: '',
                    RSD42: false,
                    RSD42ErrMsg: '',
                    RSD41: false,
                    RSD41ErrMsg: '',
                    FIN_DISP: false,
                    FIN_DISPErrMsg: '',
                    RSD43: false,
                    RSD43ErrMsg: '',
                    LIC_NTS: false,
                    LIC_NTSErrMsg: '',
                }
            } else if (
                category_digital_name.includes('licensed-practical-nurse') &&
                formsData?.[digital_name]?.values?.RSD83 === 'Y' &&
                formsData[digital_name].auxiliary.addtForms?.length === 0
            ) {
                schema.yup = object({
                    RSD83: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RSD40: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RSD42: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RSD41: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    FINAL_DISP: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RSD43: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    LIC_NTS: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    RSD83: false,
                    RSD83ErrMsg: '',
                    RSD40: false,
                    RSD40ErrMsg: '',
                    RSD42: false,
                    RSD42ErrMsg: '',
                    RSD41: false,
                    RSD41ErrMsg: '',
                    FINAL_DISP: false,
                    FINAL_DISPErrMsg: '',
                    RSD43: false,
                    RSD43ErrMsg: '',
                    LIC_NTS: false,
                    LIC_NTSErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.values?.RSD83 === 'Y' &&
                formsData[digital_name].auxiliary.addtForms?.length > 0
            ) {
                const addtForms = formsData[digital_name].auxiliary.addtForms
                const { addtSchema, addtValid } = await generateSchema(
                    addtForms
                )
                schema.yup = object().shape({ ...addtSchema })
                schema.valid = addtValid
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateMultiFormField(
                                fields,
                                max_form_iterations,
                                el?.path
                            )
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Removal Of Additional Criminal History Component/s
    const handleSubtractAdditionalCriminalHistoryComponent = async (e) => {
        try {
            if (e?.target?.name) {
                const addtForms = [
                    ...formsData[digital_name].auxiliary.addtForms,
                ]
                const subt = parseInt(e.target.name)
                const index = addtForms?.indexOf(subt)
                if (index > -1) {
                    addtForms.splice(index, 1)
                    dispatch(
                        updateFormAuxiliary({
                            digital_name: digital_name,
                            auxiliary: {
                                addtForms: addtForms,
                            },
                        })
                    )
                    const values = subtractValuesAdditionalArrayForm(
                        fields,
                        max_form_iterations,
                        formsData?.[digital_name]?.values,
                        index
                    )
                    if (values) {
                        dispatch(
                            deleteFormData({
                                digital_name: digital_name,
                            })
                        )
                        dispatch(
                            updateFormData({
                                digital_name: digital_name,
                                values: values,
                            })
                        )
                    }
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Criminal History Component Error:',
                error
            )
            return null
        }
    }

    // Handle Removal Of Additional Criminal History Component/s
    const handleSubtractAdditionalConditionalComponent = (e) => {
        try {
            if (e?.target?.name && e?.target?.value) {
                const values = subtractValuesAdditionalConditionalForm(
                    conditions,
                    formsData?.[digital_name]?.values,
                    e?.target?.name,
                    e?.target?.value
                )
                if (values) {
                    dispatch(
                        deleteFormData({
                            digital_name: digital_name,
                        })
                    )
                    dispatch(
                        updateFormAuxiliary({
                            digital_name: digital_name,
                            auxiliary: {
                                addtForms: [],
                            },
                        })
                    )
                    dispatch(
                        updateFormData({
                            digital_name: digital_name,
                            values: values,
                        })
                    )
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Examination History Component Error:',
                error
            )
            return null
        }
    }

    // Handle Data Of All Fields + DatePicker (ID)
    const handleData = (e, id) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        } else if (e?.length > 0 && id) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [id]: format(new Date(e?.[0]), 'MM/dd/yyyy'),
                    },
                })
            )
            revalidation(id)
        }
        //// Start Watson Assistant Journey

        if (e?.target?.name === 'RSD83' && e?.target?.value === 'Y') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Criminal History Convicted Yes Tour'
            )
        } else {
            instance.tours.endTour()
        }

        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Criminal History: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Criminal History: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Criminal History: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Criminal History Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'Registered Nursing Endorsement Criminal History Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else if (
        formsData?.[digital_name]?.values?.RSD83 === undefined ||
        formsData?.[digital_name]?.values?.RSD83 === 'N'
    ) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        The Board of Nursing strongly encourages all individuals
                        with criminal, discipline, and/or health history to be
                        fully prepared with information regarding their
                        background, to start the application process early, and
                        allow up to 90 days waiting time if a board appearance
                        is determined to be required upon completion of your
                        application.
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.RSD83
                                ? 'extra-text-required invalid'
                                : 'extra-text-required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.RSD83
                        }
                        valueSelected={formsData?.[digital_name]?.values?.RSD83}
                        legendText={RSD83Label()}
                        name="RSD83"
                        invalid={formsData?.[digital_name]?.errors?.RSD83ErrMsg}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD83ErrMsg
                        }
                    >
                        <RadioButton
                            id="RSD83True"
                            name="RSD83True"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="RSD83False"
                            name="RSD83False"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    } else if (
        category_digital_name.includes('registered-nurse') &&
        formsData?.[digital_name]?.values?.RSD83 === 'Y'
    ) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        The Board of Nursing strongly encourages all individuals
                        with criminal, discipline, and/or health history to be
                        fully prepared with information regarding their
                        background, to start the application process early, and
                        allow up to 90 days waiting time if a board appearance
                        is determined to be required upon completion of your
                        application.
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.RSD83
                                ? 'extra-text-required invalid'
                                : 'extra-text-required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.RSD83
                        }
                        valueSelected={formsData?.[digital_name]?.values?.RSD83}
                        legendText={RSD83Label()}
                        name="RSD83"
                        invalid={formsData?.[digital_name]?.errors?.RSD83ErrMsg}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD83ErrMsg
                        }
                    >
                        <RadioButton
                            id="RSD83True"
                            name="RSD83True"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="RSD83False"
                            name="RSD83False"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <p>Add a separate record for each incident.</p>

                    <TextInput
                        className="required"
                        labelText="Offense:"
                        id="RSD40"
                        name="RSD40"
                        placeholder="Offense"
                        value={formsData?.[digital_name]?.values?.RSD40}
                        maxLength="60"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.RSD40}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD40ErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="Jurisdiction:"
                        id="RSD42"
                        name="RSD42"
                        placeholder="Jurisdiction"
                        value={formsData?.[digital_name]?.values?.RSD42}
                        maxLength="60"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.RSD42}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD42ErrMsg
                        }
                    />

                    <DatePicker
                        dateFormat="m/d/Y"
                        datePickerType="single"
                        onChange={(e) => handleData(e, 'RSD41')}
                        onClose={(e) => handleData(e, 'RSD41')}
                        value={formsData?.[digital_name]?.values?.RSD41}
                    >
                        <DatePickerInput
                            className="required"
                            labelText="Date of Offense:"
                            id="RSD41"
                            name="RSD41"
                            placeholder="MM/DD/YYYY"
                            type="text"
                            invalid={formsData?.[digital_name]?.errors?.RSD41}
                            invalidText={
                                formsData?.[digital_name]?.errors?.RSD41ErrMsg
                            }
                        />
                    </DatePicker>

                    <TextInput
                        className="required"
                        labelText="Final Disposition:"
                        id="FIN_DISP"
                        name="FIN_DISP"
                        placeholder="Final Disposition"
                        value={formsData?.[digital_name]?.values?.FIN_DISP}
                        maxLength="20"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.FIN_DISP}
                        invalidText={
                            formsData?.[digital_name]?.errors?.FIN_DISPErrMsg
                        }
                    />

                    <Select
                        className="required"
                        defaultValue={formsData?.[digital_name]?.values?.RSD43}
                        value={formsData?.[digital_name]?.values?.RSD43}
                        helper=""
                        labelText="Under Appeal?"
                        id="RSD43"
                        name="RSD43"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.RSD43}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD43ErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Yes" value="Y" />
                        <SelectItem text="No" value="N" />
                    </Select>

                    <TextArea
                        className="required"
                        labelText="Applicant Statement:"
                        id="LIC_NTS"
                        name="LIC_NTS"
                        placeholder="Applicant Statement"
                        value={formsData?.[digital_name]?.values?.LIC_NTS}
                        maxLength="4000"
                        type="textarea"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.LIC_NTS}
                        invalidText={
                            formsData?.[digital_name]?.errors?.LIC_NTSErrMsg
                        }
                    />

                    {formsData[digital_name].auxiliary.addtForms.map((el) => (
                        <AdditionalCriminalHistoryComponent
                            key={el}
                            number={el}
                            handleData={handleData}
                            handleSubtractAdditionalCriminalHistoryComponent={
                                handleSubtractAdditionalCriminalHistoryComponent
                            }
                            category_digital_name={category_digital_name}
                        />
                    ))}

                    <AddFormComponent digital_name={digital_name} />

                    <p>
                        <strong>
                            If you responded {`"Yes,"`} you must provide the
                            following:
                        </strong>
                    </p>

                    <p>
                        <strong>Self-Explanation, </strong> describing in detail
                        the circumstances surrounding each offense; including
                        date, city and state, charges and final results.
                    </p>

                    <p>
                        <strong>Final Dispositions and Arrest Records </strong>{' '}
                        for all Offenses. The Clerk of the Court in the
                        arresting jurisdiction will provide you with these
                        documents. Unavailability of these documents must come
                        in the form of a letter from the Clerk of the Court. for
                        all offenses. The Clerk of the Court in the arresting
                        jurisdiction will provide you with these documents.
                        Unavailability of these documents must come in the form
                        of a letter from the Clerk of the Court.
                    </p>

                    <p>
                        <strong>Completion of Sentence Documents. </strong>
                        You may obtain documents from the Department of
                        Corrections. The report must include the start date, end
                        date, and that the conditions were met.
                    </p>

                    <p>
                        Three current (written within the last year)
                        professional{' '}
                        <strong> Letters of Recommendation.</strong>
                    </p>

                    <p>
                        Documentation for this section must be uploaded, sent to
                        the Background Screening Unit at
                        MQA.BackgroundScreen@flhealth.gov, or mailed to:
                    </p>

                    <p className="fldoh-address-box">
                        Background Screening Unit
                        <br />
                        Florida Department of Health
                        <br />
                        4052 Bald Cypress Way, Bin BSU-01
                        <br />
                        Tallahassee, FL 32399
                    </p>

                    <div className="file-upload-group">
                        <div className="file-upload-group-item">
                            <FileUploadComponent />
                        </div>
                    </div>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    } else if (
        category_digital_name.includes('licensed-practical-nurse') &&
        formsData?.[digital_name]?.values?.RSD83 === 'Y'
    ) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        The Board of Nursing strongly encourages all individuals
                        with criminal, discipline, and/or health history to be
                        fully prepared with information regarding their
                        background, to start the application process early, and
                        allow up to 90 days waiting time if a board appearance
                        is determined to be required upon completion of your
                        application.
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.RSD83
                                ? 'extra-text-required invalid'
                                : 'extra-text-required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.RSD83
                        }
                        valueSelected={formsData?.[digital_name]?.values?.RSD83}
                        legendText={RSD83Label()}
                        name="RSD83"
                        invalid={formsData?.[digital_name]?.errors?.RSD83ErrMsg}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD83ErrMsg
                        }
                    >
                        <RadioButton
                            id="RSD83True"
                            name="RSD83True"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="RSD83False"
                            name="RSD83False"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <p>Add a separate record for each incident.</p>

                    <TextInput
                        className="required"
                        labelText="Offense:"
                        id="RSD40"
                        name="RSD40"
                        placeholder="Offense"
                        value={formsData?.[digital_name]?.values?.RSD40}
                        maxLength="60"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.RSD40}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD40ErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="Jurisdiction:"
                        id="RSD42"
                        name="RSD42"
                        placeholder="Jurisdiction"
                        value={formsData?.[digital_name]?.values?.RSD42}
                        maxLength="60"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.RSD42}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD42ErrMsg
                        }
                    />

                    <DatePicker
                        dateFormat="m/d/Y"
                        datePickerType="single"
                        onChange={(e) => handleData(e, 'RSD41')}
                        onClose={(e) => handleData(e, 'RSD41')}
                        value={formsData?.[digital_name]?.values?.RSD41}
                    >
                        <DatePickerInput
                            className="required"
                            labelText="Date of Offense:"
                            id="RSD41"
                            name="RSD41"
                            placeholder="MM/DD/YYYY"
                            type="text"
                            invalid={formsData?.[digital_name]?.errors?.RSD41}
                            invalidText={
                                formsData?.[digital_name]?.errors?.RSD41ErrMsg
                            }
                        />
                    </DatePicker>

                    <TextInput
                        className="required"
                        labelText="Final Disposition:"
                        id="FINAL_DISP"
                        name="FINAL_DISP"
                        placeholder="Final Disposition"
                        value={formsData?.[digital_name]?.values?.FINAL_DISP}
                        maxLength="20"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.FINAL_DISP}
                        invalidText={
                            formsData?.[digital_name]?.errors?.FINAL_DISPErrMsg
                        }
                    />

                    <Select
                        className="required"
                        defaultValue={formsData?.[digital_name]?.values?.RSD43}
                        value={formsData?.[digital_name]?.values?.RSD43}
                        helper=""
                        labelText="Under Appeal?"
                        id="RSD43"
                        name="RSD43"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.RSD43}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RSD43ErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Yes" value="Y" />
                        <SelectItem text="No" value="N" />
                    </Select>

                    <TextArea
                        className="required"
                        labelText="Applicant Statement:"
                        id="LIC_NTS"
                        name="LIC_NTS"
                        placeholder="Applicant Statement"
                        value={formsData?.[digital_name]?.values?.LIC_NTS}
                        maxLength="4000"
                        type="textarea"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.LIC_NTS}
                        invalidText={
                            formsData?.[digital_name]?.errors?.LIC_NTSErrMsg
                        }
                    />

                    {formsData[digital_name].auxiliary.addtForms.map((el) => (
                        <AdditionalCriminalHistoryComponent
                            key={el}
                            number={el}
                            handleData={handleData}
                            handleSubtractAdditionalCriminalHistoryComponent={
                                handleSubtractAdditionalCriminalHistoryComponent
                            }
                            category_digital_name={category_digital_name}
                        />
                    ))}

                    <AddFormComponent digital_name={digital_name} />

                    <p>
                        <strong>
                            If you responded {`"Yes,"`} you must provide the
                            following:
                        </strong>
                    </p>

                    <p>
                        <strong>Self-Explanation, </strong> describing in detail
                        the circumstances surrounding each offense; including
                        date, city and state, charges and final results.
                    </p>

                    <p>
                        <strong>Final Dispositions and Arrest Records </strong>{' '}
                        for all Offenses. The Clerk of the Court in the
                        arresting jurisdiction will provide you with these
                        documents. Unavailability of these documents must come
                        in the form of a letter from the Clerk of the Court. for
                        all offenses. The Clerk of the Court in the arresting
                        jurisdiction will provide you with these documents.
                        Unavailability of these documents must come in the form
                        of a letter from the Clerk of the Court.
                    </p>

                    <p>
                        <strong>Completion of Sentence Documents. </strong>
                        You may obtain documents from the Department of
                        Corrections. The report must include the start date, end
                        date, and that the conditions were met.
                    </p>

                    <p>
                        Three current (written within the last year)
                        professional{' '}
                        <strong> Letters of Recommendation.</strong>
                    </p>

                    <p>
                        Documentation for this section must be uploaded, sent to
                        the Background Screening Unit at
                        MQA.BackgroundScreen@flhealth.gov, or mailed to:
                    </p>

                    <p className="fldoh-address-box">
                        Background Screening Unit
                        <br />
                        Florida Department of Health
                        <br />
                        4052 Bald Cypress Way, Bin BSU-01
                        <br />
                        Tallahassee, FL 32399
                    </p>

                    <div className="file-upload-group">
                        <div className="file-upload-group-item">
                            <FileUploadComponent />
                        </div>
                    </div>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default CriminalHistoryForm
